import { identityActionTypes } from '../identity-action-types';
import axios from 'axios';
import { BASE_URL } from '../../../../config/environmentConfig';
import { FORM_FIELDS, getFormattedUsersData } from '../../../constants/users/userModalUtils';
import { successAlert } from '../../../components/ToastMessage';
import {
	getFormattedConfirmPasswordFormFields, getFormattedResetPasswordFormFields,
	IConfirmPasswordFields, IResetPasswordFields
} from '../../../constants/confirmAccount/confirm-account-form';
import {
	getFormattedChangePasswordFormFields
} from '../../../constants/profile-settings/account-security/change-password-form';
import * as React from 'react';
import { IAction } from '../identity-interfaces';
import { IRemoveTwoFactorAuth } from '../../../components/users/UserListToggle';
import { POST } from '../../store-helper';
import { IUserFilters } from '../../../components/users/UserFilterSection';

interface IFetchUsersList {
	dispatch: React.Dispatch<IAction>,
	searchParams?: IUserFilters,
	usersListPaginationTokens?: string[],
	currentPage?: number,
}

export const fetchUsersList = async ({ dispatch, searchParams, usersListPaginationTokens, currentPage }: IFetchUsersList): Promise<void> => {
	try {
		dispatch({ type: identityActionTypes.START_USERS_LIST_LOADING });

		const pageToken = usersListPaginationTokens?.[currentPage - 2];
		const params = {
			//eslint-disable-next-line
			filter: searchParams?.query ? `${searchParams?.filterBy} ^= \"${searchParams?.query}\"` : null,
			limit: 60,
			paginationToken: pageToken || null
		};

		const res = await POST({
			pathname: `${BASE_URL.IDENTITY}/users`,
			body: params
		});

		let data = [];
		if (res?.users) {
			data = res?.users;
		}

		if (searchParams?.query) {
			usersListPaginationTokens?.splice(0, usersListPaginationTokens?.length);
		}

		if (res?.paginationToken) {
			if (currentPage > usersListPaginationTokens?.length || usersListPaginationTokens?.length === 0) {
				usersListPaginationTokens?.push(res?.paginationToken);
			}
			dispatch({ type: identityActionTypes.USER_NEXT_PAGE, payload: true });
		} else {
			dispatch({ type: identityActionTypes.USER_NEXT_PAGE, payload: false });
		}
		dispatch({ type: identityActionTypes.FETCH_USERS_LIST, payload: data });

	} catch (e) {
		console.log(e);
		dispatch({ type: identityActionTypes.FETCH_FAIL });
	}
};

export const fetchUser = async (dispatch: React.Dispatch<IAction>, email: string): Promise<void> => {
	try {
		dispatch({ type: identityActionTypes.SET_USER_DATA_LOADING, payload: true });

		const data = await POST({
			pathname: `${BASE_URL.IDENTITY}/users/user`,
			body: { email },
		});

		dispatch({ type: identityActionTypes.FETCH_USER_DATA, payload: data });
	} catch (e) {
		console.log('fetchUser error', e);
		dispatch({ type: identityActionTypes.FETCH_USER_DATA, payload: null });
	}
};

interface IUserParams {
	firstName: string,
	lastName: string,
	email: string,
	phone: string | number,
	merchantId?: string,
	group?: string,
	username?: string,
}

export const setUser = async (
	dispatch: React.Dispatch<IAction>,
	formik: any,
	onSuccess: () => void,
	onError: () => void,
	userRole: string,
	isAdmin: boolean
): Promise<void> => {
	dispatch({ type: identityActionTypes.START_CREATING_USER_LOADING });
	if (!formik.isValid) return;
	try {
		const params: IUserParams = getFormattedUsersData(formik.values);
		if (isAdmin) {
			params.merchantId = formik.values?.[FORM_FIELDS.MERCHANT_ID];
		}
		params.group = userRole;
		params.username = formik.values[FORM_FIELDS.EMAIL];

		await POST({
			pathname: `${BASE_URL.IDENTITY}/users/create`,
			body: params
		});

		await fetchUsersList({ dispatch });
		onSuccess();
		dispatch({ type: identityActionTypes.END_CREATING_USER_LOADING });
	} catch (e) {
		console.log(e);
		onError();
		dispatch({ type: identityActionTypes.END_CREATING_USER_LOADING });

	}
};

export const setUserDetails = async (formik: any, userRole: string, isAdmin: boolean): Promise<void> => {
	const params: IUserParams = getFormattedUsersData(formik.values);
	params.group = userRole;

	if (isAdmin) {
		params.merchantId = formik.values?.[FORM_FIELDS.MERCHANT_ID];
	}

	await POST({
		pathname: `${BASE_URL.IDENTITY}/users/update`,
		body: params
	});

};

export const deleteUser = async (
	userName: string,
	onSuccess: () => void,
	onError: () => void,
): Promise<void> => {
	try {

		await POST({
			pathname: `${BASE_URL.IDENTITY}/users/revoke?userName=${userName}`,
		});

		onSuccess();
	} catch (e) {
		console.log('deleteUser error', e);
		onError();
	}
};

export const removeTwoFactorAuthentication = async (params: IRemoveTwoFactorAuth): Promise<void> => {
	try {
		await axios.post(`${BASE_URL.IDENTITY}/identity/disable-users-mfa`, params);
		successAlert({
			title: 'Two-factor authentication was successfully removed!'
		});
	} catch (e) {
		console.log(e);
	}
};

export const unblockUser = async (
	email: string,
	onSuccess: (email: string) => void,
	onError: () => void
): Promise<void> => {
	try {
		// Use the dedicated unblock endpoint with email in request body
		await POST({
			pathname: `${BASE_URL.IDENTITY}/users/unblock`,
			body: {
				email: email
			}
		});

		onSuccess(email);
	} catch (error) {
		console.log('Unblock user error', error);
		onError();
	}
};

export const resetPassword = async (
	dispatch: React.Dispatch<IAction>,
	formik: any,
	userId: string,
	onSuccess: () => void,
	onError: () => void
): Promise<void> => {
	dispatch({ type: identityActionTypes.START_CHANGE_PASSWORD_LOADING });

	try {

		const params: IResetPasswordFields = getFormattedResetPasswordFormFields(formik.values);
		console.log('formik.values', userId, formik.values, params);
		await axios.post(`${BASE_URL.IDENTITY}/identity/setPassword`, {
			username: userId,
			password: params?.password
		});
		onSuccess();
		dispatch({ type: identityActionTypes.END_CHANGE_PASSWORD_LOADING });
	} catch (e) {
		console.log(e);
		onError();
		dispatch({ type: identityActionTypes.FETCH_FAIL });
	}
};

export const setInitialUserList = (dispatch: React.Dispatch<IAction>): void => {
	dispatch({ type: identityActionTypes.FETCH_USERS_LIST, payload: [] });

};
